import {
    RequestFilter,
    RequestSearchParamsDTO,
    RequestSortDirection,
    RequestSortField,
    RequestTableViewStateDTO
} from "@/api/data-contracts";
import {OverviewState} from "@/common/overview-state/overview-state-model";
import {FilterFieldGroup} from "@/common/overview-state/filter-fields";
import {REQUEST_OVERVIEW_COLUMN_IDS} from "@/common/overview-table/overview-table-column-ids";

/**
 * The opaque filter object `useOverviewTableState` carries for both request-search variants
 * (`RequestFilter.INTERNAL` / `RequestFilter.EXTERNAL`). Structurally the persisted document minus `size`
 * — `size` is a generically known `OverviewState` field, not part of the filter object (see
 * `overview-state-model.ts`). `requestStatuses` and `quoteStatuses` sit side by side exactly as in the
 * document: INTERNAL only ever populates `requestStatuses`, EXTERNAL only ever populates `quoteStatuses`
 * (`BidderOverviewPage` reads `quoteStatuses`, see the plan's "Standing constraints" — the mapping below
 * must not swap the two).
 */
export type RequestOverviewFilters = Omit<RequestTableViewStateDTO, "size">;

/**
 * The Sheet's business field groups shared by both request-search variants (badge counting, Phase 2.1):
 * carton grade, FEFCO, customer number, requesting-company name, internal dimensions (all six contract
 * fields in one group — see the plan's "Standing constraints", "Der Badge zählt den angewendeten Stand"),
 * quantity, net price, total price, valid-until, delivery period, print image, blueprint, unread messages
 * (constraint 12 of the plan: the info column now carries three field groups). Each variant adds
 * only its own status field on top (see `REQUEST_FILTER_FIELD_GROUPS_INTERNAL` /
 * `REQUEST_FILTER_FIELD_GROUPS_EXTERNAL` below) — never the other variant's status field. Every `columnId`
 * here is one of `REQUEST_OVERVIEW_COLUMN_IDS`, the TanStack column id `RequestOverviewTable.tsx` derives
 * from its `accessorKey` (or sets explicitly for "info"), shared through `overview-table-column-ids.ts` so
 * the two sides cannot drift apart.
 */
const REQUEST_FILTER_FIELD_GROUPS_COMMON: ReadonlyArray<FilterFieldGroup<RequestOverviewFilters>> = [
    {kind: "text", fields: ["cartonGradeStartsWith"], columnId: REQUEST_OVERVIEW_COLUMN_IDS.cartonGrade},
    {kind: "triState", fields: ["isProGroupCartonGrade"], columnId: REQUEST_OVERVIEW_COLUMN_IDS.cartonGrade},
    {kind: "text", fields: ["fefcoCodeStartsWith"], columnId: REQUEST_OVERVIEW_COLUMN_IDS.fefcoCode},
    {
        kind: "text",
        fields: ["customerNumberContains"],
        columnId: REQUEST_OVERVIEW_COLUMN_IDS.customerRequestNumber
    },
    {kind: "text", fields: ["requesterCompanyContains"], columnId: REQUEST_OVERVIEW_COLUMN_IDS.requesterCompany},
    {
        kind: "range",
        fields: [
            "internalDimensionLengthMin",
            "internalDimensionLengthMax",
            "internalDimensionWidthMin",
            "internalDimensionWidthMax",
            "internalDimensionHeightMin",
            "internalDimensionHeightMax"
        ],
        columnId: REQUEST_OVERVIEW_COLUMN_IDS.internalDimensions
    },
    {kind: "range", fields: ["quantityMin", "quantityMax"], columnId: REQUEST_OVERVIEW_COLUMN_IDS.quantityOptions1},
    {kind: "range", fields: ["netPriceMin", "netPriceMax"], columnId: REQUEST_OVERVIEW_COLUMN_IDS.quantityOptions2},
    {
        kind: "range",
        fields: ["totalPriceMin", "totalPriceMax"],
        columnId: REQUEST_OVERVIEW_COLUMN_IDS.quantityOptions3
    },
    {kind: "range", fields: ["validUntilFrom", "validUntilTo"], columnId: REQUEST_OVERVIEW_COLUMN_IDS.validUntil},
    {
        kind: "range",
        fields: ["deliveryDateFrom", "deliveryDateTo"],
        columnId: REQUEST_OVERVIEW_COLUMN_IDS.deliveryDateRange
    },
    {kind: "triState", fields: ["hasPrintImages"], columnId: REQUEST_OVERVIEW_COLUMN_IDS.info},
    {kind: "triState", fields: ["hasBluePrints"], columnId: REQUEST_OVERVIEW_COLUMN_IDS.info},
    {kind: "triState", fields: ["hasUnreadMessages"], columnId: REQUEST_OVERVIEW_COLUMN_IDS.info}
];

/**
 * INTERNAL's own groups: only `requestStatuses` as the status field on top of the common groups. The
 * Innendienst-only company restriction, formerly a separate `companyIds` group here, is gone; the
 * requesting-company name is now filtered through the shared `requesterCompanyContains` text group in
 * `REQUEST_FILTER_FIELD_GROUPS_COMMON`, rendered as the "Anfragesteller" column filter for Innendienst
 * roles (Phase 2.1).
 */
export const REQUEST_FILTER_FIELD_GROUPS_INTERNAL: ReadonlyArray<FilterFieldGroup<RequestOverviewFilters>> = [
    {kind: "statusSelection", fields: ["requestStatuses"], columnId: REQUEST_OVERVIEW_COLUMN_IDS.status},
    ...REQUEST_FILTER_FIELD_GROUPS_COMMON
];

/**
 * EXTERNAL's own groups: `quoteStatuses` as the status field, plus the supplier-only distance range on top
 * of the common groups. `requesterCompanyContains` now lives in `REQUEST_FILTER_FIELD_GROUPS_COMMON`, both
 * variants filter the requesting company by the same contains-search field.
 */
export const REQUEST_FILTER_FIELD_GROUPS_EXTERNAL: ReadonlyArray<FilterFieldGroup<RequestOverviewFilters>> = [
    {kind: "statusSelection", fields: ["quoteStatuses"], columnId: REQUEST_OVERVIEW_COLUMN_IDS.status},
    ...REQUEST_FILTER_FIELD_GROUPS_COMMON,
    {kind: "range", fields: ["distanceKmMin", "distanceKmMax"], columnId: REQUEST_OVERVIEW_COLUMN_IDS.distanceMeters}
];

/** Picks the right group list for a variant — callers never branch on `RequestFilter` themselves. */
export function requestFilterFieldGroupsFor(
    variant: RequestFilter
): ReadonlyArray<FilterFieldGroup<RequestOverviewFilters>> {
    return variant === RequestFilter.INTERNAL
        ? REQUEST_FILTER_FIELD_GROUPS_INTERNAL
        : REQUEST_FILTER_FIELD_GROUPS_EXTERNAL;
}

/**
 * Maps the persisted document onto the opaque filter object for one request-search variant. A missing
 * document (`undefined`, the 204-empty-body case — see `overview-state-model.ts`) yields `defaults`
 * unchanged. In a present document, a missing status field means "never touched" and falls back to
 * `defaults`; an empty status list means "nothing selected" and is kept as an empty list — `??` only
 * substitutes on `null`/`undefined`, never on an empty array, which is exactly this distinction.
 *
 * Both status fields are stripped from the document before the variant-specific one is written back, so a
 * document that (incorrectly) carries the other variant's field never leaks into the result.
 *
 * A stored `hasUnreadMessages: false` is a legacy value from the retired three-way selection (Alle/Ja/Nein):
 * the control now only ever writes `true` or leaves the field untouched, so a persisted `false` is normalized
 * to `undefined` here, hydrating to "Alle" instead of restricting the result list against a control that can
 * no longer express it.
 */
export function requestDocumentToFilters(
    doc: RequestTableViewStateDTO | undefined,
    variant: RequestFilter,
    defaults: RequestOverviewFilters
): RequestOverviewFilters {
    if (doc === undefined) {
        return defaults;
    }
    const {size: _size, requestStatuses: _requestStatuses, quoteStatuses: _quoteStatuses, ...rest} = doc;
    const hasUnreadMessages = rest.hasUnreadMessages === false ? undefined : rest.hasUnreadMessages;
    if (variant === RequestFilter.INTERNAL) {
        return {...rest, hasUnreadMessages, requestStatuses: doc.requestStatuses ?? defaults.requestStatuses};
    }
    return {...rest, hasUnreadMessages, quoteStatuses: doc.quoteStatuses ?? defaults.quoteStatuses};
}

/**
 * Maps state onto the document that gets persisted (`PUT /api/table-view-states/requests/{view}`).
 * `searchText`, `page`, `sortField` and `sortDirection` are never part of `RequestOverviewFilters` in the
 * first place (Phase 0, Entscheidung 1 for sorting; the plan's "Standing constraints" for search text and
 * page), so this mapping cannot accidentally leak them — it only adds back `size`, which the document
 * carries but `OverviewState` keeps as its own generic field rather than inside `filters`.
 */
export function requestStateToDocument(
    state: OverviewState<RequestOverviewFilters, RequestSortField>
): RequestTableViewStateDTO {
    return {...state.filters, size: state.size};
}

/**
 * Maps state onto the parameters sent to `POST /api/requests` for one request-search variant: the
 * persisted-relevant part (`filters`, `size`) plus the volatile part (`searchText`, `page`, `sortField`,
 * `sortDirection`) that is never written to the document. An empty free-text search is sent as `undefined`,
 * not `""` — the first query after loading an untouched document must show `searchText: undefined`, not an
 * empty string (see the plan's DoD-to-test mapping table).
 */
export function requestStateToSearchParams(
    state: OverviewState<RequestOverviewFilters, RequestSortField>,
    variant: RequestFilter
): RequestSearchParamsDTO {
    return {
        ...state.filters,
        type: variant,
        page: state.page,
        size: state.size,
        searchText: state.searchText === "" ? undefined : state.searchText,
        sortField: state.sortField,
        sortDirection: state.sortDirection as RequestSortDirection | undefined
    };
}
